/**
 * The language switch OUTSIDE a rave — the platform's shell and its public
 * pages.
 *
 * The same interaction the old festival drawer had (`visitor/Drawer.tsx`, deleted 2026-09-29): a
 * globe, the word "Language", and one pill per language, the chosen one
 * pressed. Choosing is immediate; there is nothing to confirm.
 *
 * **Offered only when there is a choice.** `useLocale().available` is the
 * languages the interface has words in (English and Hebrew since step 73), so
 * with one it renders nothing — a switch with one option is furniture. A
 * choice here keeps the region and is saved on the account, as the settings'
 * "Language and region" does (`LanguageSettings.tsx`).
 */
import { useLocale } from './LocaleProvider';
import { languageName, useChooseLocale } from './LanguageSettings';
import { useT } from '@/i18n';

/** More languages than this are a list, not a row of pills. */
const PILLS = 3;

export function LanguageSwitch() {
  const tr = useT();
  // A choice here is the same as one in the account's settings: saved on the
  // account too, keeping the region (step 73).
  const { language, region, available } = useLocale();
  const choose = useChooseLocale();
  if (available.length < 2) return null;

  return (
    <div role="group" aria-label={tr('Language')} className="app-lang">
      <span aria-hidden="true" className="app-lang-icon">
        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" />
        </svg>
      </span>
      <span className="app-lang-label">{tr('Language')}</span>
      {/* Two or three languages read as pills; more (step 73: twenty-two)
          as one list, each in its own name. */}
      {available.length <= PILLS ? (
        <div className="app-lang-options">
          {available.map((l) => (
            <button
              key={l}
              type="button"
              lang={l}
              onClick={() => choose(`${l}-${region}`)}
              aria-pressed={l === language}
              className="ui-toggle"
            >
              {languageName(l)}
            </button>
          ))}
        </div>
      ) : (
        <select
          className="app-lang-select"
          aria-label={tr('Language')}
          value={language}
          onChange={(e) => choose(`${e.target.value}-${region}`)}
        >
          {available.map((l) => <option key={l} value={l} lang={l}>{languageName(l)}</option>)}
        </select>
      )}
    </div>
  );
}
